import { useCallback, useEffect, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "./internal.js";
/** Reactive `matchMedia`, SSR-safe (there is no SSR today; this is cheap). */
export function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => typeof window === "undefined" ? false : window.matchMedia(query).matches);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const list = window.matchMedia(query);
        const onChange = () => setMatches(list.matches);
        onChange();
        list.addEventListener("change", onChange);
        return () => list.removeEventListener("change", onChange);
    }, [query]);
    return matches;
}
/** §3.5 LOCKED — components that animate in JS must consult this. */
export function useReducedMotion() {
    return useMediaQuery("(prefers-reduced-motion: reduce)");
}
/**
 * Body scroll lock, reference-counted.
 *
 * Overlays nest in practice (the guest log-out dialog opens the conversion
 * sheet), and two components each restoring `overflow` on unmount is how a page
 * ends up permanently unscrollable.
 */
let scrollLocks = 0;
export function useScrollLock(active) {
    useEffect(() => {
        if (!active || typeof document === "undefined")
            return;
        scrollLocks += 1;
        document.body.classList.add("wr-scroll-locked");
        return () => {
            scrollLocks -= 1;
            if (scrollLocks <= 0) {
                scrollLocks = 0;
                document.body.classList.remove("wr-scroll-locked");
            }
        };
    }, [active]);
}
/**
 * Focus trap + restore for an overlay (§15.6).
 *
 * Returns a **callback ref** to put on the overlay's container. When the trap is
 * active and the container exists, focus moves inside it; `Tab` cycles inside;
 * when the trap goes inactive (or unmounts) focus returns to whatever had it
 * when the overlay opened — which is the trigger, because the trigger is what
 * the user clicked.
 *
 * ── Why a callback ref, and not the `RefObject` this used to return (T-414) ──
 *
 * The old shape placed focus **once**, from a passive effect, against
 * `ref.current` — and silently did nothing when that was `null`. It was `null`
 * for every overlay in this file, because they all render through
 * `OverlayPortal`, whose host used to be created in an effect: the first commit
 * put no container in the tree at all, so the parent's effect focused nothing
 * and no later commit ever tried again. Measured by QA on the deployed build
 * (T-407 D3): the shop dialog and the conversion sheet both opened with
 * `document.activeElement` still on the trigger, one `Tab` away from the
 * *scrim*, so the trap trapped nothing until the player tabbed into it.
 * T-404's hand-built container had focused correctly for exactly one reason —
 * it portalled synchronously — which is a property no consumer should have to
 * know it depends on.
 *
 * A callback ref removes the dependency instead of re-timing it: React calls it
 * with the node **whenever the container arrives**, in whatever commit that
 * turns out to be, and this hook places focus then. `OverlayPortal` is also
 * fixed (its host exists from the first render), so today the node is there for
 * the arming layout effect — but a future container that mounts a commit late,
 * or swaps element (the `BottomSheet` sheet ⇄ dialog swap), can no longer lose
 * focus without a test noticing.
 *
 * "Place focus" never *steals* it: if focus is already inside the container the
 * hook leaves it alone, so a re-render or a container swap cannot yank the
 * player back to the first control.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/**
 * Can this element take focus *right now*?
 *
 * Deliberately the same selector the trap navigates by, asked of one node: a
 * control that is still in the container but `disabled` is not a place focus
 * can be, and the containment guard below has to tell that apart from a
 * control that is merely between focus events (T-417 NB1).
 */
function canTakeFocus(element) {
    return element.matches(FOCUSABLE);
}
/**
 * Give focus back on close: the trigger if it can still take it, else the
 * caller's fallback.
 *
 * **Tried, then verified.** `element.focus()` is a silent no-op on a detached
 * or undrawn element, so "did it take?" is asked of `document.activeElement`
 * afterwards rather than assumed from the element looking plausible. That is
 * the only form of the check that survives the browser/jsdom difference this
 * bug lived in.
 */
function restoreFocusOnClose(preferred, fallback) {
    if (typeof document === "undefined")
        return;
    const offered = fallback?.() ?? null;
    const candidates = [
        preferred,
        ...(Array.isArray(offered) ? offered : [offered]),
    ];
    for (const candidate of candidates) {
        // `<body>` is where focus already is when nothing took it; focusing it is
        // not a restoration, it is the failure this exists to notice.
        if (!candidate || !candidate.isConnected || candidate === document.body)
            continue;
        candidate.focus?.();
        if (document.activeElement === candidate)
            return;
    }
}
export function useFocusTrap(active, options) {
    const nodeRef = useRef(null);
    const restoreTo = useRef(null);
    /**
     * Held in a ref, and read only at close: a consumer passing an inline arrow
     * must not re-arm the trap on every render (that would re-capture
     * `restoreTo` from inside the overlay and lose the trigger).
     */
    const restoreFallback = useRef(options?.restoreFallback);
    restoreFallback.current = options?.restoreFallback;
    /** True between the arming layout effect and its cleanup. */
    const armed = useRef(false);
    /**
     * The last control **inside** the overlay that had focus. Load-bearing for the
     * containment guard below — see the comment there; without it the guard cannot
     * tell "the focused control was removed" from "the browser is mid-`Tab`".
     */
    const lastInside = useRef(null);
    /**
     * True while the trap is *holding* focus on the container itself because
     * nothing inside could take it (T-417 NB1). The container is a holding
     * position, never a destination: as soon as a real control exists again the
     * guard hands focus over, which is how §5.4's "focus moves to Close in the
     * same frame it replaces the confirm" survives an in-flight window.
     *
     * Only focus the trap itself parked counts. A player who clicks the dialog's
     * own inert text also lands on the container (it is the nearest focusable
     * ancestor), and that is a place they chose to be.
     */
    const parked = useRef(false);
    const placeFocus = useCallback((node) => {
        // Already inside: a re-render is not a reason to move the player's focus.
        if (node.contains(document.activeElement))
            return;
        const first = node.querySelector(FOCUSABLE);
        // The container itself is the fallback — it carries `tabIndex={-1}` for
        // exactly this case, so an overlay with nothing focusable in it still puts
        // focus inside the `aria-modal` container rather than leaving it outside.
        const target = first ?? node;
        // Recorded here as well as from `focusin`, because the trap's own opening
        // focus happens *before* that listener is installed.
        lastInside.current = target;
        target.focus();
    }, []);
    const setNode = useCallback((node) => {
        nodeRef.current = node;
        if (node && armed.current)
            placeFocus(node);
    }, [placeFocus]);
    /**
     * Layout, not passive — see the note in `internal.ts`. Two things
     * depend on it: the `Tab` listener is live in the commit the overlay becomes
     * visible, and `restoreTo` captures the trigger *before* anything moves
     * focus. (Refs attach earlier in the same commit than the parent's effects
     * run, which is why `setNode` refuses to focus until this has armed: it must
     * not run before the trigger has been recorded.)
     */
    useIsomorphicLayoutEffect(() => {
        if (!active || typeof document === "undefined")
            return;
        restoreTo.current = document.activeElement;
        armed.current = true;
        if (nodeRef.current)
            placeFocus(nodeRef.current);
        function onKeyDown(event) {
            if (event.key !== "Tab" || !nodeRef.current)
                return;
            const container = nodeRef.current;
            const items = Array.from(container.querySelectorAll(FOCUSABLE));
            /**
             * **Nothing inside can take focus — and `Tab` still must not leave
             * (T-417 NB1).**
             *
             * §5.5 disables *both* of the purchase dialog's buttons while a commit is
             * in flight, so for that window the container's focusable set is empty.
             * This handler used to return here, which let the browser run its own
             * sequential navigation and walk onto the cards behind the scrim — QA
             * measured six consecutive presses landing outside the modal, every one
             * `insideModal: false`.
             *
             * The container itself holds focus in that window (it carries
             * `tabIndex={-1}` and the guard below puts focus on it), so suppressing
             * the default keeps the player exactly where they are with nothing moving
             * — and the moment a control comes back, `Tab` from the container reaches
             * it normally.
             */
            if (items.length === 0) {
                if (container.contains(document.activeElement))
                    event.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            /**
             * **Focus is on the container itself** — the trap parked it there while
             * the contents were inert, or the player clicked the panel's own text.
             * Forwards, the browser would reach the first control inside anyway;
             * **backwards it would leave the overlay entirely**, because the previous
             * tabbable in document order is behind the scrim. Both directions are
             * therefore answered here, and both stay inside.
             */
            if (document.activeElement === container) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
                return;
            }
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
        /** Remember where focus has been inside the overlay, for the guard below. */
        function onFocusIn(event) {
            const target = event.target;
            if (target && nodeRef.current?.contains(target))
                lastInside.current = target;
            // Focus that lands anywhere other than the container ends the hold.
            if (target !== nodeRef.current)
                parked.current = false;
        }
        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("focusin", onFocusIn, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("focusin", onFocusIn, true);
            armed.current = false;
            lastInside.current = null;
            parked.current = false;
            restoreFocusOnClose(restoreTo.current, restoreFallback.current);
        };
    }, [active, placeFocus]);
    /**
     * **Focus that is LOST comes back; focus that was MOVED is left alone.**
     *
     * An overlay replaces its own body while it is open — the purchase dialog
     * swaps the confirm for a Close on a refusal, the conversion sheet swaps the
     * whole form for its success panel. Removing the focused element does not move
     * focus to a sibling; it drops it on `<body>`, which is *outside* the modal,
     * and from there the next `Tab` walks the page behind the scrim. The trap's
     * own `Tab` handler cannot save that, because it only acts at the container's
     * first and last control.
     *
     * So this runs after **every** commit while the trap is armed, and it acts on
     * two conditions together. A consumer that deliberately moves focus to a
     * particular control (§5.4's "focus moves to Close in the same frame it
     * replaces the confirm") is never second-guessed — `placeFocus` is a no-op when
     * focus is already inside, and this guard does not fire at all when focus is
     * somewhere real.
     *
     * ── The second condition, and why "nothing has focus" is not enough ─────────
     *
     * `document.activeElement === body` is ALSO true for a few microseconds in the
     * middle of an ordinary `Tab`: the browser fires `focusout` on the old control
     * with `body` active, and only then moves focus to the next one. If anything
     * re-renders inside that gap — and `CredentialsFields` does, it tracks blur —
     * a guard that only asked "is focus nowhere?" fires there and pulls focus back
     * to the first control, so **`Tab` stops working**. Measured on the deployed
     * staging build with this guard's first shape: `focusout auth-email ->
     * related=auth-password active=BODY`, immediately followed by
     * `focusin auth-email`, on every press. jsdom cannot see it — it has no
     * sequential focus navigation at all — which is precisely why the ticket asks
     * for a live drive.
     *
     * So the guard also requires that the control that had focus is **gone** —
     * where "gone" means *gone as a place focus can be*, not merely absent from
     * the DOM. `lastInside` still being in the container **and still focusable**
     * means this is a transition, not a loss, and the browser is about to land
     * focus on its own.
     *
     * ── The third state, which is neither (T-417 NB1) ───────────────────────────
     *
     * A control that is **still in the container and now `disabled`** is a loss
     * wearing a transition's clothes. §5.5 disables the purchase dialog's buttons
     * for the length of the commit; Chromium drops focus from a disabled element
     * onto `<body>` and fires nothing else, so the old two-condition guard
     * declined — `lastInside` was right there — and the modal sat with focus
     * outside it for the whole in-flight window. (jsdom never blurs a disabled
     * element, which is why T-414's test for this passed while the deployed build
     * failed; the tests that cover it now force the blur.)
     *
     * That case *parks* focus on *the container itself*, not on the first control:
     * the control the player was on is coming back in a moment, and moving them
     * to some other control while it does would be a focus move nobody asked for.
     * The container is inside the `aria-modal` boundary and carries `tabIndex={-1}`
     * for exactly this (it is already `placeFocus`'s fallback for an overlay with
     * nothing focusable in it).
     *
     * **And a parked hold is released as soon as a real control exists**, which is
     * what keeps §5.4 LOCKED true — *"focus moves to Close in the same frame it
     * replaces the confirm"*. Before this ticket that came about by accident: focus
     * had been dropped on `<body>`, so the swap looked like a loss and `placeFocus`
     * ran. Now the in-flight window ends with focus on the container, and the hold
     * is what carries the player to whatever the overlay puts back.
     */
    useIsomorphicLayoutEffect(() => {
        if (typeof document === "undefined")
            return;
        const node = nodeRef.current;
        if (!armed.current || !node)
            return;
        const focused = document.activeElement;
        if (parked.current && focused === node) {
            const first = node.querySelector(FOCUSABLE);
            if (!first)
                return;
            parked.current = false;
            lastInside.current = first;
            first.focus();
            return;
        }
        if (focused !== null && focused !== document.body)
            return;
        const last = lastInside.current;
        if (last && node.contains(last)) {
            if (canTakeFocus(last))
                return;
            parked.current = true;
            lastInside.current = node;
            node.focus();
            return;
        }
        placeFocus(node);
    });
    return setNode;
}
/**
 * `Esc` closes every overlay (§15.6).
 *
 * Layout, not passive: the key that dismisses an overlay must work from the
 * first moment the overlay is on screen. See the note in `internal.ts` —
 * this is the fix for the Esc-dismiss flake family, and the reason it is a fix
 * rather than a longer timeout.
 */
export function useEscape(active, onEscape) {
    const handler = useRef(onEscape);
    handler.current = onEscape;
    useIsomorphicLayoutEffect(() => {
        if (!active || typeof document === "undefined")
            return;
        function onKeyDown(event) {
            if (event.key === "Escape")
                handler.current();
        }
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [active]);
}
/**
 * A countdown in whole seconds, for `CountdownAction` and the lockout banner.
 *
 * Ticks on a 1s interval and stops at zero. The caller re-arms it by passing a
 * new `seconds` value — that is what makes "resend resets the timer" a state
 * change rather than a second timer.
 */
export function useCountdown(seconds) {
    const [deadline, setDeadline] = useState(() => Date.now() + seconds * 1000);
    const [remaining, setRemaining] = useState(seconds);
    useEffect(() => {
        setDeadline(Date.now() + seconds * 1000);
        setRemaining(seconds);
    }, [seconds]);
    useEffect(() => {
        if (remaining <= 0)
            return;
        const timer = setInterval(() => {
            const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            setRemaining(left);
            if (left <= 0)
                clearInterval(timer);
        }, 250);
        return () => clearInterval(timer);
    }, [deadline, remaining]);
    const restart = useCallback(() => {
        setDeadline(Date.now() + seconds * 1000);
        setRemaining(seconds);
    }, [seconds]);
    return { remaining, restart };
}
/* ==========================================================================
   The room's countdown derivation — ONE of them, for every renderer
   ========================================================================== */
/**
 * **The cadence.** 250 ms, and it is deliberately faster than the thing it
 * draws: a 1 s interval against a `Math.ceil`'d second drifts against the true
 * boundary by up to a second, which is visible as a stuttering number. Four
 * samples a second is also §10.1's repaint ceiling, and React bails out of the
 * commit when `Math.ceil` produces the same integer — so a running countdown
 * writes the DOM once a second, not four times.
 */
export const COUNTDOWN_TICK_MS = 250;
/**
 * `Math.ceil` to whole seconds, floored at zero.
 *
 * S4 room-spec amendment 2026-08-18 clause (c): *"`Math.ceil` to whole seconds,
 * so a 20 s window reads `0:20` on arrival and reaches `0:00` only at the
 * deadline"*; clause (d): *"floor at `0:00` and hold — never negative."*
 */
function remainingSeconds(deadlineAt, now) {
    if (deadlineAt === null)
        return 0;
    return Math.max(0, Math.ceil((deadlineAt - now) / 1000));
}
/**
 * **The room's one countdown derivation** — S4 room-spec amendment 2026-08-18
 * (`PREROUND-COUNTDOWN-TICK`), clause (a): *"one derivation, two renderers. The
 * sentence's `{time}` token and the `Countdown` component read the same ticking
 * source; **do not write a second timer** — reuse `Countdown`'s existing 250 ms
 * cadence."*
 *
 * This hook **is** that cadence. It was `Countdown`'s private body until the
 * amendment; lifting it here rather than copying it is the whole point — the
 * alternative is every caller running a timer and §11.2's freeze rule being
 * re-implemented per screen, which is how one countdown on a frozen screen ends
 * up still ticking. The same discipline S5 §6.1(c) clause 3 requires of the
 * last-ten-seconds beat: *"no second timer, ever."*
 *
 * The clauses this hook owns, so that no renderer has to:
 *
 *  - **(b) anchoring** is the caller's `deadlineAt` — a **client-clock** epoch
 *    produced by `deadlineToClientClock(deadline, serverTime, receivedAt)`. Each
 *    patch produces a new number, which lands in the effect's dep list, so the
 *    re-anchor is silent and immediate (`tick()` runs before the interval is
 *    armed). After T-426 stamped every published write, a re-anchor no longer
 *    rewinds.
 *  - **(c) `Math.ceil`**, via `remainingSeconds`.
 *  - **(d) floor at `0:00` and hold.** Zero is a terminal reading, not a signal
 *    to stop rendering: nothing here unmounts anything, and the phase change is
 *    the server's to announce.
 *  - **(e) an absent deadline** (`null`) is `0` — a caller draws nothing for it,
 *    and there is no last value to hold either.
 *  - **(f) `frozen` stops it, holding the last value** (§11.2 LOCKED). The
 *    interval is not merely ignored, it is **not scheduled**: a stale transport
 *    delivers no patches, so a `setInterval` that kept running would be the one
 *    live thing on a dead screen. `held` is the last value the tick produced,
 *    which is what "holds its last value" means — not a recomputation.
 *
 * (g) — announcement — is a renderer's business, not this hook's: it returns a
 * number and touches no DOM.
 */
export function useCountdownSeconds(deadlineAt, frozen = false) {
    const [seconds, setSeconds] = useState(() => remainingSeconds(deadlineAt, Date.now()));
    /** The last value rendered before the freeze. Held, not recomputed. */
    const held = useRef(seconds);
    useEffect(() => {
        if (frozen)
            return;
        const tick = () => {
            const next = remainingSeconds(deadlineAt, Date.now());
            held.current = next;
            setSeconds(next);
        };
        tick();
        const timer = setInterval(tick, COUNTDOWN_TICK_MS);
        return () => clearInterval(timer);
    }, [deadlineAt, frozen]);
    /**
     * **A frozen countdown holds its last value; a countdown with no deadline has
     * no last value to hold** — game spec §4.2, LOCKED: *"after the round ends and
     * while §5.9's reveal holds, the value reads `0:00`."*
     *
     * `GameWrapper` stops its timer by passing `deadlineAt = null` **and**
     * `frozen = true` (the round is over: it must neither tick nor be described as
     * paused). Without this line the freeze won the tie and the held value was
     * whatever the clock last read — which is `0:00` when a round runs out of time
     * and is **not** when the server closes it early (A52's every-word-found
     * finish, now the common case). `ROUND OVER · 0:07` is a live-looking clock on
     * a finished round, and it is the same class of claim §11.2's freeze exists to
     * prevent.
     *
     * `remainingSeconds(null, …)` is already `0`; this only stops `frozen` from
     * overriding it.
     */
    return deadlineAt === null ? 0 : frozen ? held.current : seconds;
}
//# sourceMappingURL=hooks.js.map